import { adjustForIframeIfNecessary, findIframeForEditor, isElementInEditorDocument, isEditorInIframe } from './iframe';
export function hoverInfoHasData(hoverInfo) {
  return !!hoverInfo.nearestInsertionPoint || !!hoverInfo.anchorLinkPoint;
}
export function hoverInfo(e, editorView, composerIdentifier) {
  // Get the client coordinates
  let {
    clientX,
    clientY
  } = e;
  let element = e.target;

  // If we are hovering over the parent window we transpose the clientX and clientY so they are inside the editor rect
  if (isEditorInIframe(editorView) && !isElementInEditorDocument(element, editorView)) {
    let iframeRect = findIframeForEditor(editorView)?.getBoundingClientRect();
    if (iframeRect) {
      clientX -= iframeRect.x;
      clientY -= iframeRect.y;
    }
  }

  // Get editor rect coordinates
  let editorRect = editorView.dom.getBoundingClientRect();

  // Is the editor currently being hovered?
  let editorHovered = isElementWithinEditor(e.target, editorView, {
    left: clientX,
    top: clientY
  }, composerIdentifier);

  // ClientCoords are used for debug mode
  let clientCoords = adjustForIframeIfNecessary(new DOMRect(clientX, clientY, 4, 4), editorView);

  // Get the hovered element from the client coordinates
  let hoveredElement = editorView.dom.ownerDocument.elementFromPoint(clientX, clientY);

  // RETURN if nothing is currently hovered
  if (!hoveredElement || !editorView.dom.contains(hoveredElement)) {
    return {
      nearestInsertionPoint: null,
      editorRect: adjustForIframeIfNecessary(editorRect, editorView),
      editorHovered,
      clientCoords
    };
  }

  // Find hovered heading
  let anchorLinkPoint = findAnchorLinkPoint(editorView, hoveredElement);
  // Find closest insertable container e.g main editor or tables
  let insertableContainer = findInsertableContainerForElement(hoveredElement);
  if (!insertableContainer) {
    return {
      nearestInsertionPoint: null,
      editorRect: adjustForIframeIfNecessary(editorRect, editorView),
      anchorLinkPoint,
      editorHovered,
      clientCoords
    };
  }

  // Find the borders off children of insertable container and find the closest based on the clientY value
  let containerRect = insertableContainer.getBoundingClientRect();
  let possiblePositions = findPossiblePositions(insertableContainer);
  let closestY = pickClosestPosition(possiblePositions, clientY);

  // Generate custom rect for insertion point
  let rect = new DOMRect(containerRect.left, closestY, containerRect.width, 1);
  // Get the `pos` from customer
  let insertionPointIndex = possiblePositions.indexOf(closestY);
  let pos = editorView.posAtDOM(insertableContainer, insertionPointIndex, -1);

  // Is the insertion point in the editor viewport
  let visible = isVisible(rect, editorRect);

  // Adjust the coordinates of the editor to the coordinates of the parent window (just in case we're in an iframe)
  rect = adjustForIframeIfNecessary(rect, editorView);
  editorRect = adjustForIframeIfNecessary(editorRect, editorView);
  if (pos < 0) {
    return {
      nearestInsertionPoint: null,
      editorRect,
      anchorLinkPoint,
      editorHovered,
      clientCoords
    };
  }
  return {
    nearestInsertionPoint: {
      rect,
      pos,
      visible
    },
    editorRect,
    anchorLinkPoint,
    hoveredElement: hoveredElement,
    editorHovered,
    clientCoords
  };
}
function isElementWithinEditor(element, editorView, coords, composerIdentifier) {
  return Boolean(editorView.posAtCoords(coords)) || elementIsWithinPointerElement(element, composerIdentifier);
}
function elementIsWithinPointerElement(element, composerIdentifier) {
  do {
    if (element?.dataset.composerPointer === composerIdentifier) {
      return true;
    }
  } while (element = !element ? null : element.parentElement);
  return false;
}
function findInsertableContainerForElement(element) {
  let insertableContainer = element;
  while (insertableContainer && !insertableContainer.getAttribute('data-insertable')) {
    insertableContainer = insertableContainer.parentElement;
  }
  return insertableContainer;
}
function findPossiblePositions(container) {
  let containerRect = container.getBoundingClientRect();
  let possiblePositions = [];
  if (container.firstElementChild) {
    possiblePositions.push((containerRect.top + container.firstElementChild.getBoundingClientRect().top) / 2);
  }
  Array.from(container.children).forEach(child => {
    let childRect = child.getBoundingClientRect();
    if (child.nextElementSibling) {
      let siblingRect = child.nextElementSibling?.getBoundingClientRect();
      if (siblingRect) {
        possiblePositions.push((siblingRect.top + childRect.bottom) / 2);
      }
    } else {
      possiblePositions.push(Math.min((containerRect.bottom + childRect.bottom) / 2, childRect.bottom + 10));
    }
  });
  return possiblePositions;
}
function pickClosestPosition(positions, clientY) {
  return positions.reduce(function (prev, curr) {
    return Math.abs(curr - clientY) < Math.abs(prev - clientY) ? curr : prev;
  });
}
function isVisible(rect, editorRect) {
  return rect.top < editorRect.bottom && rect.bottom > editorRect.top;
}
function findHoveredHeading(hoveredElement) {
  let element = hoveredElement;
  while (element !== null && !element.classList.contains('ProseMirror')) {
    if (['H1', 'H2', 'H3', 'H4'].includes(element.tagName)) {
      return element;
    }
    element = element.parentElement;
  }
  return null;
}
function findAnchorLinkPoint(editorView, hoveredElement) {
  let hoveredAnchorLink = findHoveredHeading(hoveredElement);
  if (!hoveredAnchorLink) {
    return;
  }
  let hoveredElementPos = editorView.posAtDOM(hoveredAnchorLink, 0);
  let resolvedPos = editorView.state.doc.resolve(hoveredElementPos);
  let hoveredNode = resolvedPos.node(resolvedPos.depth);
  return {
    pos: hoveredElementPos,
    node: hoveredNode,
    domElement: hoveredAnchorLink
  };
}